<template>
  <section
    class="position-relative overflow-hidden svm-section bg-dark-3"
    :style="bgImageStyle"
    aria-label="thumbnail">
    <!-- video background desktop-->
    <iframe
      title="Background video autoplay"
      class="d-none position-absolute w-100 border-0 svm-video-iframe"
      :class="{ 'd-md-block': applyMdBlockClass }"
      :src="bgVideoUrl"></iframe>

    <!-- foreground -->
    <div
      v-if="toggleMode === 'standard'"
      class="position-relative h-100 d-flex align-items-center justify-content-center"
      :style="bgShadow">
      <div class="container d-flex flex-column justify-content-end h-100">
        <div class="row">
          <div class="col-12 col-lg-8 col-xl-6">
            <h2
              class="titleStandard"
              :class="[
                { 'pb-0': isLabelEmpty },
                'pb-space-sm',
                'pb-lg-space-md',
                titleClass,
              ]"
              v-html="title"></h2>
            <h3
              v-if="!isLabelEmpty"
              class="pb-space-sm pb-lg-space-md"
              :class="labelClass"
              v-html="label"></h3>
          </div>
          <div
            class="col-12 col-lg-4 col-xl-6 align-self-end d-flex justify-content-lg-end pb-space-sm pb-lg-space-md">
            <button
              class="btn btn-dark-1 modal-play-button pe-space-xxs d-flex align-items-center"
              @click="handleModalOpen($event)"
              :class="modalTextClass">
              <span class="me-space-xxs">{{ modalText }}</span>
              <img :src="VideoButton" alt="videoButton" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- foreground2 -->
    <div
      v-if="toggleMode === 'custom'"
      class="position-relative h-100 d-flex align-items-center justify-content-center"
      :style="bgShadow">
      <div class="container d-flex flex-column justify-content-center h-100">
        <div class="row justify-content-center" style="text-align: center">
          <div class="col-12 col-lg-8 col-xl-6">
            <h2
              class="pb-space-sm pb-lg-space-md"
              :class="titleClass"
              v-html="title"></h2>
            <h3
              class="pb-space-sm pb-lg-space-md"
              :class="labelClass"
              v-html="label"></h3>
          </div>
        </div>
        <div class="row">
          <div
            class="col-12 align-self-end d-flex mb-space-sm mb-lg-space-xl"
            style="justify-content: center">
            <button
              class="btn btn-dark-1 modal-play-button fs-small d-flex align-items-center pe-space-xxs"
              @click="handleModalOpen($event)"
              :class="modalTextClass">
              <span class="me-space-xxs">{{ modalText }}</span>
              <img alt="videoButton" :src="VideoButton" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <rds-modal
      :visible="showModal"
      centered
      exterior-close-btn
      :size="modalSize"
      body-class="p-0"
      :aria-label="'Play Video: ' + modalText"
      @hidden="handleModalClose($event)"
      bg-variant="transparent">
      <div class="ratio ratio-16x9">
        <iframe
          title="Video"
          class="w-100 h-100"
          :src="modalVideoUrl"
          ref="video"></iframe>
      </div>
    </rds-modal>
  </section>
</template>

<script setup lang="ts">
import { RdsModal } from "@rds-vue-ui/rds-modal";
import { computed, onMounted, ref } from "vue";
import VideoButton from "../section-video-apollo/assets/Video-button.svg";

interface Props {
  youtubeId: string;
  bgImageSource: string;
  bgShadowOpacity?: number;
  title: string;
  label?: string;
  titleSize?: "small" | "medium" | "large" | "xl";
  labelSize?: "small" | "medium" | "large" | "xl";
  titleVariant?: string;
  labelVariant?: string;
  modalText?: string;
  modalTextSize?: "small" | "medium" | "large" | "xl";
  modalTextVariant?: string;
  toggleMode?: "standard" | "custom";
  applyMdBlockClass?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  labelSize: "small",
  titleVariant: "white",
  labelVariant: "light-4",
  modalText: "Watch video",
  modalTextSize: "small",
  modalTextVariant: "secondary",
  bgShadowOpacity: 0.8,
  toggleMode: "standard",
  applyMdBlockClass: true,
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when the modal is closed.
   */
  (e: "modalClose", event: Event): void;
  /**
   *  Emitted when the modal is opened.
   */
  (e: "modalOpen", event: Event): void;
}>();

const showModal = ref(false);
const video = ref<HTMLIFrameElement | null>();
const modalSize = ref<"xl" | "sm" | "md" | "lg">("xl");

const bgImageStyle = computed(() => {
  return {
    "background-image": "url(" + props.bgImageSource + ")",
  };
});

const bgVideoUrl = computed(() => {
  const { youtubeId } = props;
  const youtubeEmbedBaseUrl: string = "https://www.youtube.com/embed/";
  return (
    youtubeEmbedBaseUrl +
    youtubeId +
    "?autoplay=1&loop=1&mute=1&controls=0&cc_load_policy=0&disablekb=1&iv_load_policy=3modestbranding=1&rel=0&playlist=" +
    youtubeId
  );
});

const modalVideoUrl = computed<string>(() => {
  const { youtubeId } = props;
  const youtubeEmbedBaseUrl: string = "https://www.youtube.com/embed/";

  return youtubeEmbedBaseUrl + youtubeId;
});

const bgShadow = computed(() => {
  return {
    "background-color": "rgba(0,0,0, " + props.bgShadowOpacity + ")",
  };
});

const titleClass = computed(() => {
  return `h2-${props.titleSize} text-${props.titleVariant}`;
});

const labelClass = computed(() => {
  return `h3-${props.labelSize} text-${props.labelVariant}`;
});

const modalTextClass = computed(() => {
  return `fs-${props.modalTextSize} text-${props.modalTextVariant}`;
});

const handleModalOpen = (event: Event) => {
  showModal.value = true;
  emits("modalOpen", event);
};

const handleModalClose = (event: Event) => {
  video.value!.src = video.value!.src;
  showModal.value = false;
  emits("modalClose", event);
};

const updateModalSize = () => {
  const width = window.innerWidth;
  if (width < 1366) {
    modalSize.value = "lg"; // for small devices and windows
  } else {
    modalSize.value = "xl"; // Extra large for desktop
  }
};

const isLabelEmpty = computed(() => {
  return !props.label || props.label.trim() === "";
});

onMounted(() => {
  updateModalSize();
  window.addEventListener("resize", updateModalSize);
});
</script>

<style lang="scss" scoped>
.svm-video-iframe {
  aspect-ratio: 16/9;
}

.svm-section {
  height: calc(300px + (680 - 300) * ((100vw - 320px) / (1280 - 320)));
  width: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.svm-modal-close-btn {
  right: -3%;
}

.modal-play-button {
  img {
    border: 1px var(--rds-light-4, #d0d0d0) solid;
    border-radius: 50%;
    color: var(--rds-dark-3, #191919);
    background: var(--rds-white, #ffffff);
    opacity: 0.7;
    transition:
      transform 400ms,
      opacity 400ms;
  }
}

@media screen and (max-width: 1200px) {
  .svm-modal-close-btn {
    right: -8%;
  }
}

@media screen and (max-width: 992px) {
  .modal-play-button svg {
    height: 7rem;
    width: 7rem;
    padding: 2.5rem;
  }
}

@media screen and (max-width: 768px) {
  .svm-modal-close-btn {
    right: -10%;
  }
  .modal-play-button svg {
    padding: 2rem;
  }
}

@media screen and (max-width: 575px) {
  .svm-section {
    height: 300px;
  }
  .svm-modal-close-btn {
    right: 0;
    top: -15%;
  }
  .modal-play-button svg {
    padding: 1.25rem;
  }
}
</style>
